/**
 * CR-7352: how many collections are waiting on the signed-in user.
 *
 * Read by every Quality tab strip so the "My Collections" tab carries its
 * own backlog, and by the queue page itself after a row is completed. The
 * endpoint is a bare COUNT, which is why it is cheap enough to call on each
 * of those pages rather than being threaded through a context.
 *
 * CR-5889: also returns `covering`, how many of them belong to someone the
 * user covers for while that person is out of office.
 *
 * @param enabled - false on the supplier side, where the queue does not exist
 * @returns the open count, how many are covered work, and a refresh callback
 *   to call after a change
 */
import { useCallback, useEffect, useState } from 'react';

import { qualityAPI } from '../../lib/api';

export function useMyCollectionsCount(
  enabled: boolean,
): { count: number; covering: number; refresh: () => void } {
  const [count, setCount] = useState(0);
  const [covering, setCovering] = useState(0);

  const refresh = useCallback(() => {
    if (!enabled) return;
    qualityAPI
      .getMyCollectionsCount()
      .then((res) => {
        setCount(res.data?.count ?? 0);
        setCovering(res.data?.covering ?? 0);
      })
      // A failed count must not break the page it decorates — the tab simply
      // shows no number.
      .catch(() => undefined);
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { count, covering, refresh };
}
